import React, { useState } from 'react';
import { BsToggleOn, BsToggleOff } from 'react-icons/bs';
import { FaTag } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { url } from '../../util/config';

const drawerStyle = {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '400px',
    height: '100%',
    background: '#fff',
    boxShadow: '-2px 0 8px rgba(0,0,0,0.13)',
    zIndex: 9999,
    padding: '24px 24px 0 24px',
    overflowY: 'auto',
    transition: 'transform 0.3s',
};

const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(0,0,0,0.2)',
    zIndex: 9998,
};

const fileUploadBoxStyle = {
    border: '2px dashed #007bff',
    borderRadius: '8px',
    padding: '16px',
    textAlign: 'center',
    background: '#f8f9fa',
    cursor: 'pointer',
    marginBottom: '16px'
};

const DataConverseDrawer = ({
    open,
    onClose,
    data,
    updateFilesLocally,
    updateFileStatusLocally
}) => {
    const [uploading, setUploading] = useState(false);
    const [fileError, setFileError] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);

    if (!open || !data) return null;

    // File upload
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        validateAndSetFile(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            validateAndSetFile(e.dataTransfer.files[0]);
        }
    };

    const validateAndSetFile = (file) => {
        if (!file) {
            setFileError('File is required');
            setSelectedFile(null);
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setFileError('Max file size is 5MB');
            setSelectedFile(null);
            return;
        }
        setSelectedFile(file);
        setFileError('');
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setFileError('Please select a file');
            return;
        }
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/dataconverse/${data._id}/files`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
            if (!response.ok) throw new Error('Failed to upload file');
            const result = await response.json();
            const uploadedFile =
            result.files && Array.isArray(result.files)
                ? result.files[0]
                : result.files;
            // Fallback for missing keys
            const safeFile = {
                _id: uploadedFile?._id || Math.random().toString(),
                originalName: uploadedFile?.originalName || uploadedFile?.name || uploadedFile?.filename || 'Unknown File',
                status: typeof uploadedFile?.status === 'boolean' ? uploadedFile.status : true,
                ...uploadedFile,
            };
            updateFilesLocally(data._id, [safeFile]);
            toast.success('File uploaded!', { autoClose: 2000 });
            setSelectedFile(null);
        } catch (err) {
            toast.error('Error uploading file', { autoClose: 2000 });
        }
        setUploading(false);
    };

    // Toggle file status
    const handleFileStatusToggle = async (fileId, currentStatus) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/dataconverse/${data._id}/file/${fileId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                }
            });
            if (!response.ok) throw new Error('Failed to update file status');
            updateFileStatusLocally(fileId, !currentStatus);
            toast.success('File status updated!', { autoClose: 2000 });
        } catch {
            toast.error('Error updating file status', { autoClose: 2000 });
        }
    };

    return (
        <>
            <div style={overlayStyle} onClick={onClose}></div>
            <div style={drawerStyle}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 style={{ fontWeight: 700 }}>{data.title}</h4>
                    <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Close</button>
                </div>
                <div className="mb-2">
                    <strong>Description:</strong>
                    <div style={{ color: '#555', marginBottom: 8 }}>{data.description}</div>
                </div>
                <div className="mb-2">
                    <FaTag style={{ color: '#6c757d', marginRight: 8 }} />
                    <span style={{ fontWeight: 500, color: '#333' }}>{data.metatag}</span>
                </div>
                <div className="mb-2">
                    <strong>Status:</strong>
                    <span className={`badge ms-2 ${data.status ? 'bg-success' : 'bg-danger'}`}>
                        {data.status ? 'Active' : 'Inactive'}
                    </span>
                </div>
                <div className="mb-2">
                    <strong>Vector Status:</strong>
                    <span className={`badge ms-2 ${data.vectorStatus ? 'bg-success' : 'bg-warning text-dark'}`}>
                        {data.vectorStatus ? 'Active' : 'Inactive'}
                    </span>
                </div>
                <div className="mb-2">
                    <strong>Files:</strong>
                    <ul className="list-group mb-2">
                        {Array.isArray(data.files) && data.files.length > 0 ? (
                            data.files.map(file => (
                                <li key={file?._id || Math.random()} className="list-group-item d-flex justify-content-between align-items-center">
                                    <span>
                                        {/* Safely access file name, fallback to 'Unknown File' */}
                                        {(file && (file.originalname || file.originalName || file.name || file.filename)) || 'Unknown File'}
                                        <span className={`badge ms-2 ${file?.status ? 'bg-success' : 'bg-danger'}`}>
                                            {file?.status ? 'Active' : 'Inactive'}
                                        </span>
                                    </span>
                                    <button
                                        className="btn btn-sm"
                                        title={file?.status ? 'Deactivate' : 'Activate'}
                                        onClick={() => file && handleFileStatusToggle(file._id, file.status)}
                                        style={{ background: '#f0f0f0' }}
                                    >
                                        {file?.status
                                            ? <BsToggleOff style={{ fontSize: "20px", color: "red" }} />
                                            : <BsToggleOn style={{ fontSize: "20px", color: "green" }} />}
                                    </button>
                                </li>
                            ))
                        ) : (
                            <li className="list-group-item text-muted">No files uploaded.</li>
                        )}
                    </ul>
                </div>
                <div className="mb-2">
                    <strong>Add More Files:</strong>
                    <div
                        style={fileUploadBoxStyle}
                        onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                        onDrop={handleDrop}
                        onClick={() => document.getElementById('drawer-file-input').click()}
                    >
                        <input
                            type="file"
                            id="drawer-file-input"
                            style={{ display: 'none' }}
                            accept=".pdf,.txt,.doc,.docx"
                            onChange={handleFileChange}
                        />
                        <div>
                            {selectedFile
                                ? <span className="text-success">{selectedFile.name}</span>
                                : <span className="text-muted">Drag & drop or click to select a file (.pdf, .txt, .doc, .docx, max 5MB)</span>
                            }
                        </div>
                    </div>
                    <small className={fileError ? 'text-danger' : 'text-muted'}>
                        {fileError ? fileError : ''}
                    </small>
                    <button
                        className="btn btn-primary btn-sm mt-2"
                        disabled={uploading}
                        onClick={handleUpload}
                    >
                        {uploading ? 'Uploading...' : 'Upload'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default DataConverseDrawer;